import React, { useState } from 'react';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { getStatusColor, getStatusText } from '../utils';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials } from 'utils/commonUtils';
import { RelatedEstimate } from 'containers/jobs/jobs.model';
import { EstimateStatus } from '../constants';
import { RedCloseIcon, RelatedEstimateIcon, VerticalListActionsIcon } from 'assets/icons';
import { CustomPopover, If } from 'components';

interface RelatedEstimateCardProps {
  estimate: RelatedEstimate;
  onEstimateClick: (id: string) => void;
  onRemoveEstimate?: (id: string) => void;
  className?: string;
  fromBillingTab?: boolean;
}

const RelatedEstimateCard: React.FC<RelatedEstimateCardProps> = ({
  estimate,
  onEstimateClick,
  onRemoveEstimate,
  className = '',
  fromBillingTab = false
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const togglePopover = () => {
    setIsPopoverOpen(prev => !prev);
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };

  return (
    <div
      className={`${className} flex w-full cursor-pointer items-center justify-between 
        rounded border border-borderGray py-3 px-4`}
      onClick={e => {
        e.stopPropagation();
        onEstimateClick(estimate.id);
      }}>
      <div className="flex w-3/5 items-start gap-3">
        <RelatedEstimateIcon className="shrink-0" />

        <div className="w-2/3 grow">
          <div className="text-sbase text-tertiaryText">{estimate.estimateNumber}</div>
          <If condition={!fromBillingTab}>
            <div className="max-w-[90%] break-words text-sbase text-tertiaryText">{estimate.title}</div>
          </If>
          <div className="flex items-center gap-2">
            <div className="text-px13 text-accentText">
              {new Date(estimate.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </div>
            <div className="h-1 w-1 rounded-full bg-accentText"></div>
            <div>
              <CustomAvatar src={estimate.createdBy.profilePicture} width="w-4" height="h-4" fontStyle="text-[8px]">
                {getInitials(estimate.createdBy.name)}
              </CustomAvatar>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <div className={'text-17px font-bold text-primaryText'}>{getAmountWithCurrency(estimate.amount)}</div>
          <div
            className={'text-right text-px13 capitalize'}
            style={{ color: getStatusColor(estimate.status as EstimateStatus) }}>
            {getStatusText(estimate.status as EstimateStatus)}
          </div>
        </div>
        <If condition={estimate.hasPermission && !fromBillingTab}>
          <CustomPopover
            anchorComponent={() => (
              <VerticalListActionsIcon
                height={20}
                width={4}
                className={'mx-2 flex shrink-0 cursor-pointer'}
                id="remove-estimate-option-click"
              />
            )}
            show={isPopoverOpen}
            onTogglePopover={togglePopover}
            onClosePopover={handleClosePopover}
            offset="-right-1"
            minWidth="min-w-[180px]"
            showArrow={true}
            highlightAnchor={true}
            padding="p-3">
            <div
              className="flex cursor-pointer items-center gap-2 hover:bg-highlightGray"
              onClick={e => {
                e.stopPropagation();
                onRemoveEstimate(estimate.id);
                togglePopover();
              }}>
              <RedCloseIcon />
              <div className="text-sbase font-semibold text-primaryText">Remove estimate</div>
            </div>
          </CustomPopover>
        </If>
      </div>
    </div>
  );
};

export default RelatedEstimateCard;
